import { Card, CardContent, Typography, Box } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'realizado',
})(({ theme, realizado }) => ({
  position: 'relative',
  height: '120px',
  width: '100%',
  minWidth: '70px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: realizado ? 'default' : 'pointer',
  transition: 'all 0.2s ease-in-out',
  border: `2px solid ${realizado ? theme.palette.success.main : 'rgba(0, 0, 0, 0.12)'}`,
  backgroundColor: realizado ? 'rgba(16, 185, 129, 0.1)' : theme.palette.background.paper,
  borderRadius: '14px',
  touchAction: 'manipulation',
  WebkitTapHighlightColor: 'transparent',
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  boxSizing: 'border-box',
  '&:hover': {
    transform: realizado ? 'none' : 'translateY(-2px)',
    boxShadow: realizado 
      ? '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.06)'
      : '0px 4px 6px -1px rgba(0, 0, 0, 0.08), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  '&:active': {
    transform: realizado ? 'none' : 'translateY(0) scale(0.98)',
  },
}));

const CheckIcon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 8,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0px 2px 4px rgba(16, 185, 129, 0.3)',
  zIndex: 10,
  pointerEvents: 'none',
}));

export function BicoButton({ bico, realizado, valor, onClick }) {
  const tipoCombustivel = bico.tipoCombustivel || bico.TipoCombustivel || {};
  const apelidoCombustivel = tipoCombustivel.apelido || tipoCombustivel.Apelido || tipoCombustivel.nome || tipoCombustivel.Nome || 'N/A';
  const numeroBico = bico.numero || bico.Numero || bico.id;
  
  // Formatar número do bico como B01, B02, etc.
  const numeroFormatado = `B${String(numeroBico).padStart(2, '0')}`;

  return (
    <motion.div
      whileHover={!realizado ? { scale: 1.02 } : {}}
      whileTap={!realizado ? { scale: 0.98 } : {}}
      style={{ width: '100%' }}
    >
      <Box
        component="div"
        onClick={realizado ? undefined : onClick}
        sx={{
          p: 0,
          textTransform: 'none',
          width: '100%',
          cursor: realizado ? 'default' : 'pointer',
        }}
      >
        <StyledCard realizado={realizado} sx={{ width: '100%' }}>
          {realizado && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <CheckIcon>
                <CheckCircle sx={{ fontSize: 16 }} />
              </CheckIcon>
            </motion.div>
          )}
          <CardContent 
            sx={{ 
              p: { xs: 1.5, sm: 1.5 }, 
              '&:last-child': { pb: { xs: 1.5, sm: 1.5 } },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              width: '100%',
              height: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Typography
              variant="h5"
              component="div"
              fontWeight="bold"
              color="text.primary"
              sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem' }, 
                textAlign: 'center',
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              {numeroFormatado}
            </Typography>
            <Typography
              variant="caption"
              component="div"
              color="text.secondary"
              textTransform="uppercase"
              sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.75rem' }, 
                fontWeight: 500, 
                textAlign: 'center',
                lineHeight: 1.2,
                mb: realizado && valor !== undefined ? 0.5 : 0,
                minHeight: realizado && valor !== undefined ? 'auto' : '20px',
              }}
            >
              {apelidoCombustivel}
            </Typography>
            <Box
              sx={{
                minHeight: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 0.5,
              }}
            >
              {realizado && valor !== undefined ? (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  style={{ textAlign: 'center', width: '100%' }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="success.main"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, textAlign: 'center', lineHeight: 1.2 }}
                  >
                    {valor >= 0 ? '+' : ''}{Math.round(valor)} ml
                  </Typography>
                </motion.div>
              ) : (
                <Box sx={{ height: '20px' }} />
              )}
            </Box>
          </CardContent>
        </StyledCard>
      </Box>
    </motion.div>
  );
}
